import React, { useState, useEffect } from 'react';
import proptypes from 'prop-types';
const { string, bool } = proptypes;

import {
  arrayOf,
  extend,
  params,
  string as stringType,
  shape as shapeType,
  useDataModel
} from '@thd-nucleus/data-sources';
import { useStore } from '@thd-nucleus/experience-context';

import {
  STORES_LIMIT,
  SEARCH_DISTANCE,
  VEHICLE_CATEGORIES,
  LARGE_EQUIPMENT_CATEGORIES,
  PRICING_NOT_FOUND_MESSAGE,
  HOISTING_LICENSE_REQUIRED_SUB_CATEGORIES
} from '../utils/constants';
import { ToolContext } from '../context/tool-context';
import { useApiData } from '../hooks/useApiData';
import { PriceBar } from './PriceBar/PriceBar';
import { LoadingPriceBar } from './Loading/LoadingPriceBar';
import { DepositCallout } from './DepositCallout/DepositCallout';
import { LoadingDepositCallout } from './Loading/LoadingDepositCallout';
import { StoreCallout } from './StoreCallout/StoreCallout';
import { LoadingStoreCallout } from './Loading/LoadingStoreCallout';
import { PriceError } from './PriceError/PriceError';

import './rental-price.scss';

export const RentalPrice = ({
  itemId,
  hideStoreInfo,
  hideDepositCallout,
  depositCalloutReduced,
  isRentalClearTool,
  isDeliveryFlow,
  deliveryOnly,
  graphQlEnabled
}) => {
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.trigger('rental-price.ready');
  }, []);

  const { data } = useDataModel('product', { variables: { itemId } });
  const experienceStore = useStore();

  const options = {
    ssr: !!experienceStore?.storeId,
    variables: {
      storeSearchInput: experienceStore?.storeId,
      radius: SEARCH_DISTANCE,
      pagesize: STORES_LIMIT
    }
  };

  const { data: storeSearchData } = useDataModel('storeSearch', options);
  const storeList = storeSearchData?.storeSearch?.stores;

  const toolContextValue = React.useMemo(() => ({
    isAVehicle: () => VEHICLE_CATEGORIES.includes(data?.product?.identifiers?.rentalCategory),
    isALargeEquipment: () => LARGE_EQUIPMENT_CATEGORIES.includes(data?.product?.identifiers?.rentalCategory),
    isHoistingLicenseRequired: () => (
      LARGE_EQUIPMENT_CATEGORIES.includes(data?.product?.identifiers?.rentalCategory)
      && HOISTING_LICENSE_REQUIRED_SUB_CATEGORIES.includes(data?.product?.identifiers?.rentalSubCategory)
    )
  }), [data]);

  const isAVehicle = toolContextValue.isAVehicle();

  const { rentalCategory, rentalSubCategory } = data?.product?.identifiers || '';

  const { pricingData, isLoading, pricingNotFound, isError } = useApiData({
    experienceStore,
    storeList,
    isAVehicle,
    refresh,
    rentalCategory,
    rentalSubCategory,
    graphQlEnabled,
    setRefresh,
    isDeliveryFlow
  });

  const showStoreInfo = () => {
    return !isLoading && !isError && !pricingNotFound
      ? <StoreCallout storeInfo={pricingData.closestStoreInfo} isDeliveryFlow={isDeliveryFlow} />
      : <LoadingStoreCallout />;
  };

  const showDepositCallout = () => {
    return (
      <>
        <hr />
        {!isLoading && !isError && !pricingNotFound ? (
          <DepositCallout
            depositCalloutReduced={depositCalloutReduced}
            isRentalClearTool={isRentalClearTool}
            isDeliveryFlow={isDeliveryFlow}
            deliveryOnly={deliveryOnly}
          />
        ) : <LoadingDepositCallout depositCalloutReduced={depositCalloutReduced} />}
      </>
    );
  };

  return (
    <ToolContext.Provider value={toolContextValue}>
      <div className="rental-price" data-component="RentalPrice">
        {!hideStoreInfo && showStoreInfo()}
        {!isLoading && !isError && !pricingNotFound
          ? <PriceBar rentalPrices={pricingData.closestPricingData?.rentalPricing} />
          : <LoadingPriceBar isDeliveryFlow={isDeliveryFlow} />}
        { isError && <PriceError refresh={refresh} setRefresh={setRefresh} />}
        { pricingNotFound && <PriceError refresh={false} setRefresh={() => {}} message={PRICING_NOT_FOUND_MESSAGE} /> }
        {!hideDepositCallout && showDepositCallout()}
      </div>
    </ToolContext.Provider>
  );
};

RentalPrice.displayName = 'RentalPrice';

RentalPrice.propTypes = {
  depositCalloutReduced: bool,
  hideDepositCallout: bool,
  hideStoreInfo: bool,
  itemId: string.isRequired,
  isRentalClearTool: bool,
  isDeliveryFlow: bool,
  deliveryOnly: bool,
  graphQlEnabled: bool
};

RentalPrice.defaultProps = {
  depositCalloutReduced: false,
  hideDepositCallout: false,
  hideStoreInfo: false,
  isRentalClearTool: false,
  isDeliveryFlow: false,
  deliveryOnly: false,
  graphQlEnabled: false
};

RentalPrice.dataModel = extend(
  {
    product: params({ itemId: stringType().isRequired(), dataSource: stringType() }).shape({
      itemId: stringType(),
      dataSources: stringType(),
      identifiers: shapeType({
        rentalCategory: stringType(),
        rentalSubCategory: stringType()
      })
    }),
    storeSearch: params({ storeSearchInput: stringType().isRequired(), pagesize: stringType() }).shape({
      stores: arrayOf(shapeType({
        storeId: stringType(),
        name: stringType(),
        phone: stringType(),
        toolRentalPhone: stringType(),
        distance: stringType()
      }))
    }),
    getRentalPricing: params({
      storeList: arrayOf(stringType()).isRequired(),
      categoryCode: stringType().isRequired(),
      subCategoryCode: stringType().isRequired()
    }).arrayOf(shapeType({
      locationNbr: stringType(),
      depositAmt: stringType(),
      dailyRate: stringType(),
      weeklyRate: stringType(),
      monthlyRate: stringType(),
      fourHourRate: stringType(),
      seventyFiveMinutesRate: stringType(),
      hourlyRate: stringType()
    }))
  }
);